import { ErrorAlert } from "@components/ErrorAlert";
import { queriesWorksites } from "@query";
import { selectorAddressLine } from "@selectors/selectorAddressLine";
import {
  useWSModalCustom,
  WSAlert,
  WSCard,
  WSDataItem,
  WSGrid,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Worksite } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

type Props = {
  worksite: Worksite;
  /** Engagements pointing at this work site — derived by the list page. */
  engagementsCount: number;
} & WSModalCustomProps<boolean>;

const ModalDeleteWorksite: React.FC<Props> = props => {
  const [deleteWorksite, meta] = queriesWorksites.deleteWorksite.useMutation();

  const isPrimary = !!props.worksite.isPrimary;
  const hasEngagements = props.engagementsCount > 0;
  // Historical data is only known to the server, so a delete that looks allowed
  // here can still be refused — that comes back through the ErrorAlert.
  const isBlocked = isPrimary || hasEngagements;

  const handleDelete = async () => {
    if (!props.worksite.id) {
      return;
    }

    await deleteWorksite(
      { worksiteId: props.worksite.id },
      { onSuccess: () => props.onClose(true) }
    );
  };

  return (
    <WSModal
      size="S"
      title="Delete work site"
      onClose={() => props.onClose(false)}
      actions={{
        buttons: isBlocked
          ? [
              {
                label: "Done",
                kind: "Secondary",
                onClick: () => props.onClose(false)
              }
            ]
          : [
              {
                label: "Cancel",
                kind: "Secondary",
                onClick: () => props.onClose(false)
              },
              {
                label: "Delete work site",
                destructive: true,
                loading: meta.isLoading,
                onClick: handleDelete
              }
            ]
      }}
    >
      <WSList gap="M">
        <WSText.ParagraphSm color="gray500">
          Are you sure you want to delete work site?
        </WSText.ParagraphSm>

        <WSText.Heading5>Work site</WSText.Heading5>

        <WSCard>
          <WSGrid gutter="M">
            <WSGrid.Item span={{ m: "6" }}>
              <WSDataItem
                label={{ text: "Work site name" }}
                value={props.worksite.name}
                secondaryText={isPrimary ? "Primary" : undefined}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ m: "6" }}>
              <WSDataItem
                label={{ text: "Address" }}
                value={selectorAddressLine(props.worksite.address) || "–"}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ m: "6" }}>
              <WSDataItem
                label={{ text: "No. of engagements" }}
                value={String(props.engagementsCount)}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ m: "6" }}>
              <WSDataItem
                label={{ text: "External ID" }}
                value={props.worksite.externalId || "–"}
              />
            </WSGrid.Item>
          </WSGrid>
        </WSCard>

        {isBlocked ? (
          <WSAlert
            theme="warning"
            icon="alert"
            title="Work site cannot be deleted when:"
          >
            <ul>
              {isPrimary ? (
                <li>
                  Primary work site cannot be deleted. Mark another site as
                  primary and try again.
                </li>
              ) : null}
              <li>Engagements exist.</li>
              <li>Historical data exists</li>
            </ul>
          </WSAlert>
        ) : null}

        <ErrorAlert context="WorksiteDelete" error={meta.error} />
      </WSList>
    </WSModal>
  );
};

export const useModalDeleteWorksite = () =>
  useWSModalCustom(ModalDeleteWorksite);
